import { ActionTree } from 'vuex'

import api from '@/apiClient'

import {
  Option,
  OptionContext,
  OptionOuterContext,
  OptionResourcePayload
} from '@/models'

import { assertToken, mergeById } from '@/utils'

import { RootState } from '../../state'
import { MenuState } from '../state'

import {
  ADD_OPTION,
  INCREMENT_OPTION_GROUP_OPTIONS_QUERY_INDEX,
  LOADED_ALL_OPTION_GROUP_OPTIONS,
  RESET_OPTION_GROUP_OPTIONS_QUERY_INDEX,
  UPDATE_OPTION_GROUP_OPTIONS,
  UPDATE_OPTION_GROUP_OPTIONS_TOTAL,
  UPDATE_OPTIONS
} from './mutations'

const actions: ActionTree<MenuState, RootState> = {
  /**
   * Adds a new Option and updates an OptionGroup relation.
   *
   * @param option Option to be added.
   * @param optionGroupId OptionGroup id in which the Option will be added.
   */
  addOption (
    { commit, state },
    { option, optionGroupId }: { option: Option; optionGroupId: number }
  ) {
    const optionGroupOptions = state.optionGroups.options[optionGroupId]

    if (optionGroupOptions === undefined) {
      throw new Error(`Options relation for OptionGroup "${optionGroupId}" was not found`)
    }

    commit(ADD_OPTION, option)

    commit(UPDATE_OPTION_GROUP_OPTIONS_TOTAL, {
      optionGroupId,
      total: optionGroupOptions.total + 1
    })

    commit(UPDATE_OPTION_GROUP_OPTIONS, {
      optionGroupId,
      options: [...optionGroupOptions.ids, option.id]
    })
  },

  /**
   * Creates an Option.
   *
   * It rejects if the user is not logged in.
   *
   * @param payload Payload to create the Option.
   */
  async createOption ({ dispatch, rootState, rootGetters }, payload: OptionResourcePayload) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const option = await api.menu.options.createOption(tokenV2, providerId, businessUnit.id, payload)

    await dispatch('addOption', { option, optionGroupId: payload.groupId })

    return option
  },

  /**
   * Loads one Option.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the OptionGroup if it is not loaded.
   *
   * @param context OptionContext to be loaded.
   */
  async loadOption (
    { commit, dispatch, rootState, rootGetters, state },
    { id, optionGroupId, itemGroupId }: OptionContext
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    let optionGroupOptions = state.optionGroups.options[optionGroupId]

    if (optionGroupOptions === undefined) {
      await dispatch('loadOptionGroup', { id: optionGroupId, itemGroupId })
      optionGroupOptions = state.optionGroups.options[optionGroupId]
    }

    const option = await api.menu.options.fetchOption(tokenV2, providerId, businessUnit.id, id)

    if (!optionGroupOptions.ids.has(option.id)) {
      await dispatch('addOption', { option, optionGroupId })
    } else {
      const options = mergeById(state.options.elements, [option])
      commit(UPDATE_OPTIONS, options)
    }
  },

  /**
   * Loads a page of Options.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the OptionGroup if it is not loaded.
   *
   * When a previously loaded page changes, it desynchronizes and will load
   * an empty page after the last page, which will reset the query index.
   *
   * @param context OptionOuterContext whose Options will be loaded.
   */
  async loadOptionPage (
    { commit, dispatch, rootState, rootGetters, state },
    { optionGroupId, itemGroupId }: OptionOuterContext
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    let optionGroupOptions = state.optionGroups.options[optionGroupId]

    if (optionGroupOptions === undefined) {
      await dispatch('loadOptionGroup', { id: optionGroupId, itemGroupId })
      optionGroupOptions = state.optionGroups.options[optionGroupId]
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.menu.options.fetchOptionPage(
      tokenV2,
      providerId,
      businessUnit.id,
      {
        ...optionGroupOptions.query,
        groupId: optionGroupId
      }
    )

    if (optionGroupOptions.total !== page.total) {
      commit(UPDATE_OPTION_GROUP_OPTIONS_TOTAL, { optionGroupId, total: page.total })
    }

    if (page.elements.length > 0) {
      const options = mergeById(state.options.elements, page.elements)

      commit(UPDATE_OPTIONS, options)

      const ids = [
        ...optionGroupOptions.ids,
        ...page.elements.map((i) => i.id)
      ]

      commit(UPDATE_OPTION_GROUP_OPTIONS, { optionGroupId, options: ids })

      if ([...new Set(ids)].length === page.total) {
        commit(LOADED_ALL_OPTION_GROUP_OPTIONS, optionGroupId)
      }

      commit(INCREMENT_OPTION_GROUP_OPTIONS_QUERY_INDEX, {
        optionGroupId,
        offset: page.elements.length
      })
    } else {
      commit(RESET_OPTION_GROUP_OPTIONS_QUERY_INDEX, optionGroupId)

      if (page.total === 0 && optionGroupOptions.ids.size > 0) {
        commit(UPDATE_OPTION_GROUP_OPTIONS, { optionGroupId, options: [] })
      }
    }
  },

  /**
   * Updates an Option.
   *
   * It rejects if the user is not logged in.
   *
   * @param id Option id to be updated.
   * @param payload Payload to update it.
   */
  async updateOption (
    { commit, rootState, rootGetters, state },
    { id, payload }: { id: number; payload: OptionResourcePayload }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const option = await api.menu.options.updateOption(tokenV2, providerId, businessUnit.id, id, payload)

    const options = mergeById(state.options.elements, [option])

    commit(UPDATE_OPTIONS, options)
  }
}

export default actions
